import { Select } from "@narmi/design_system";
import { useQueryClient } from "@tanstack/react-query";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import { $adminApi, $staffApi } from "../../../api";
import { Thread } from "../types";
import styles from "./style.module.css";

export function ThreadMetadata({ thread }: { thread: Thread }) {
  const queryClient = useQueryClient();
  const { sendNotification } = useNotificationContext();

  const { data: teamsData } = $adminApi.useSuspenseQuery("get", "/v2/admin/teams/");
  const { data: stafferData } = $staffApi.useSuspenseQuery("get", "/api/assignments/staffers/");
  const { data: staffAssignmentData } = $staffApi.useSuspenseQuery("get", "/api/assignments/", {
    params: {
      query: {
        assignment_type: "support_thread",
        assignment_id: [thread.id],
      },
    },
  });
  const { data: requestTypeData } = $staffApi.useSuspenseQuery("get", "/api/thread_categories/");

  const { mutate: updateThread, isPending: isUpdating } = $adminApi.useMutation(
    "patch",
    "/v2/admin/threads/{uuid}/",
    {
      onSuccess() {
        queryClient.invalidateQueries({
          queryKey: ["get", "/v2/admin/threads/{uuid}/"],
        });
        sendNotification({
          type: "success",
          text: "Message updated.",
        });
      },
      onError() {
        sendNotification({
          type: "negative",
          text: "Failed to update message.",
        });
      },
    }
  );

  const { mutate: assignThread, isPending: isAssigning } = $staffApi.useMutation(
    "post",
    "/api/assignments/",
    {
      onSuccess() {
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/assignments/"],
        });
        sendNotification({
          type: "success",
          text: "Message assignment updated.",
        });
        window.dispatchEvent(new CustomEvent("forceFetchInboxMessageCount"));
      },
      onError() {
        sendNotification({
          type: "negative",
          text: "Failed to update message assignment.",
        });
      },
    }
  );

  const isPending = isUpdating || isAssigning;

  const selectedRequestType = requestTypeData.results.find(
    (category) => category.uuid === thread.category
  );

  return (
    <div className={styles.userDetailsDataContainer}>
      <Select
        label="Status"
        value={thread.state}
        onChange={(state: Thread["state"]) => {
          updateThread({
            params: {
              path: {
                uuid: thread.id,
              },
            },
            body: { state },
          });
        }}
        disabled={isPending}
      >
        <Select.Item value="open">Open</Select.Item>
        <Select.Item value="waiting_for_response">Awaiting response</Select.Item>
        <Select.Item value="escalated">Escalated</Select.Item>
        <Select.Item value="closed">Closed</Select.Item>
      </Select>
      <Select
        key={`request-type-${thread.id}-${thread.updated_at}`} // Ensure re-render on thread update
        label="Request type"
        value={selectedRequestType?.uuid}
        onChange={(category: string) => {
          updateThread({
            params: {
              path: {
                uuid: thread.id,
              },
            },
            body: { category: category === "Unassigned" ? null : category },
          });
        }}
        disabled={isPending}
      >
        {selectedRequestType && <Select.Item value="Unassigned">Remove request type</Select.Item>}
        {requestTypeData.results.map((category) => (
          <Select.Item key={category.uuid} value={category.uuid}>
            {category.name}
          </Select.Item>
        ))}
      </Select>
      <Select
        label="Assignee"
        value={
          staffAssignmentData.results.find((assignment) => assignment.assignment_id === thread.id)
            ?.assignee?.id || "Unassigned"
        }
        disabled={isPending}
        onChange={(stafferId: string) => {
          assignThread({
            body: {
              assignee_id: stafferId === "Unassigned" ? null : stafferId,
              assignments: [
                {
                  id: thread.id,
                  type: "support_thread",
                  action: "assign",
                },
              ],
            },
          });
        }}
      >
        <Select.Item value="Unassigned">Unassigned</Select.Item>
        {stafferData.users.map((staffer) => (
          <Select.Item key={staffer.id} value={staffer.id}>
            {staffer.full_name}
          </Select.Item>
        ))}
      </Select>
      <Select
        label="Team"
        value={
          teamsData.results.find((team) => team.uuid === thread.assignee)?.uuid || "Unassigned"
        }
        onChange={(team: string) => {
          updateThread({
            params: {
              path: {
                uuid: thread.id,
              },
            },
            body: { assignee: team === "Unassigned" ? null : team },
          });
        }}
        disabled={isPending}
      >
        <Select.Item value="Unassigned">Unassigned</Select.Item>
        {teamsData.results.map((team) => (
          <Select.Item key={team.uuid} value={team.uuid}>
            {team.name}
          </Select.Item>
        ))}
      </Select>
    </div>
  );
}
